import { useCallback, useEffect, useRef, useMemo } from 'react';

// Utility function for class names (simple implementation)
const cn = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Performance optimization utilities
export const AnimationOptimizer = {
  isTabVisible: () => !document.hidden,
  
  requestFrame: (callback: () => void) => {
    return requestAnimationFrame(callback);
  },
  
  cancelFrame: (id: number) => {
    cancelAnimationFrame(id);
  }
};

// Hardware-accelerated class utilities
export const animationClasses = {
  // Base performance classes
  hardwareAccelerated: 'will-change-transform transform-gpu backface-hidden',
  containment: 'contain-[layout_style_paint]',
  perspective: 'perspective-1000 transform-style-preserve-3d',
  
  // Marquee animation classes
  marqueeContainer: cn(
    'overflow-x-hidden overflow-y-visible relative contain-[layout_style] will-change-transform transform-gpu',
    'py-2' // Add padding to prevent top/bottom cutoff on hover expansions
  ),
  
  marqueeContent: cn(
    'flex will-change-transform transform-gpu animate-marquee-scroll'
  ),
  
  // Particle animation classes - optimized
  particlesContainer: cn(
    'absolute inset-0 pointer-events-none',
    'contain-[layout_style_paint] will-change-contents transform-gpu',
    'backface-hidden perspective-1000'
  ),
  
  floatingParticle: cn(
    'absolute w-1 h-1 bg-primary-500/60 rounded-full',
    'will-change-transform transform-gpu',
    'backface-hidden'
  ),
  
  // Scroll indicator classes
  scrollIndicator: cn(
    'absolute bottom-8 left-1/2 z-20',
    'will-change-transform transform-gpu -translate-x-1/2 animate-scroll-bounce'
  ),
  
  scrollIndicatorInner: cn(
    'w-6 h-10 border-2 border-white rounded-full flex justify-center',
    'will-change-transform transform-gpu'
  ),
  
  scrollIndicatorDot: cn(
    'w-1 h-3 bg-white rounded-full mt-2',
    'will-change-transform transform-gpu animate-scroll-dot'
  ),
  
  // Background optimization
  optimizedBackground: cn(
    'absolute inset-0 bg-cover bg-center bg-no-repeat',
    'will-change-transform transform-gpu'
  )
};



// Marquee Animation Hook
export const useMarqueeAnimation = () => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  
  const startAnimation = useCallback(() => {
    if (!marqueeRef.current || !AnimationOptimizer.isTabVisible()) return;
    
    const marqueeElement = marqueeRef.current.querySelector('[data-marquee-content]') as HTMLElement;
    if (marqueeElement) {
      marqueeElement.style.animationPlayState = 'running';
    }
  }, []);

  const pauseAnimation = useCallback(() => {
    if (!marqueeRef.current) return;
    
    const marqueeElement = marqueeRef.current.querySelector('[data-marquee-content]') as HTMLElement;
    if (marqueeElement) {
      marqueeElement.style.animationPlayState = 'paused';
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
        } else {
          pauseAnimation();
        }
      },
      { threshold: 0.1 }
    );

    if (marqueeRef.current) {
      observer.observe(marqueeRef.current);
    }

    return () => {
      observer.disconnect();
      pauseAnimation();
    };
  }, [startAnimation, pauseAnimation]);

  return {
    marqueeRef,
    containerClasses: animationClasses.marqueeContainer,
    contentClasses: animationClasses.marqueeContent,
    startAnimation,
    pauseAnimation
  };
};

// Floating Particles Hook - Optimized with CSS-only animations
export const useFloatingParticles = (particleCount: number = 20) => {
  const particlesRef = useRef<HTMLDivElement>(null);
  const isInView = useRef(false);

  const generateParticles = useCallback(() => {
    return Array.from({ length: particleCount }, (_, index) => {
      const delay = Math.random() * 6;
      const duration = 8 + Math.random() * 4;
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      
      return {
        id: index,
        initialX: startX,
        initialY: startY,
        delay: delay,
        classes: cn(
          'absolute w-1 h-1 bg-primary-500/60 rounded-full',
          'will-change-transform transform-gpu'
        ),
        style: {
          left: `${startX}%`,
          top: `${startY}%`,
          animation: `floatOptimized ${duration}s ${delay}s ease-in-out infinite`,
          transform: 'translate3d(0, 0, 0)',
          backfaceVisibility: 'hidden' as const,
          perspective: '1000px'
        }
      };
    });
  }, [particleCount]);

  // No more RAF loop - pure CSS animations
  const animateParticles = useCallback(() => {
    // Empty - animations handled by CSS
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        isInView.current = entry.isIntersecting;
        if (particlesRef.current) {
          // Pause/resume CSS animations based on visibility
          const particles = particlesRef.current.children;
          for (let i = 0; i < particles.length; i++) {
            const particle = particles[i] as HTMLElement;
            particle.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          }
        }
      },
      { threshold: 0.1 }
    );

    if (particlesRef.current) {
      observer.observe(particlesRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Memoize particles to prevent re-generation on every render
  const particles = useMemo(() => generateParticles(), [generateParticles]);

  return {
    particlesRef,
    containerClasses: animationClasses.particlesContainer,
    particles,
    animateParticles
  };
};

// Scroll Indicator Hook
export const useScrollIndicator = () => {
  return {
    containerClasses: animationClasses.scrollIndicator,
    innerClasses: animationClasses.scrollIndicatorInner,
    dotClasses: animationClasses.scrollIndicatorDot
  };
};

// Background Optimization Hook
export const useOptimizedBackground = (imagePath: string, overlay?: string) => {
  const backgroundStyle = {
    backgroundImage: overlay 
      ? `${overlay}, url(${imagePath})`
      : `url(${imagePath})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transform: 'translateZ(0)',
    zIndex: 20
  };

  return {
    backgroundClasses: animationClasses.optimizedBackground,
    backgroundStyle
  };
};

// Responsive Animation Classes
export const responsiveAnimationClasses = {
  marqueeContent: cn(
    animationClasses.marqueeContent,
    'lg:animate-marquee-scroll md:animate-marquee-scroll-mobile'
  ),
  floatingParticle: cn(
    animationClasses.floatingParticle,
    'lg:animate-particle-float md:animate-particle-float-mobile'
  )
};

// Performance monitoring
export const usePerformanceMonitoring = (componentName: string) => {
  useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      // Performance monitoring - render time tracked
    };
  }, [componentName]);
};

// CSS Containment utility
export const CSSContainment = {
  createContainmentStyle: (types: string[]) => ({
    contain: types.join(' '),
    willChange: 'transform',
    transform: 'translateZ(0)'
  })
};

// Image optimization utility
export const ImageOptimizer = {
  createOptimizedBackgroundStyle: (imagePath: string, overlay?: string) => ({
    backgroundImage: overlay 
      ? `${overlay}, url(${imagePath})`
      : `url(${imagePath})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    willChange: 'transform',
    transform: 'translateZ(0)'
  }),
  
  preloadImages: async (imagePaths: string[]) => {
    const promises = imagePaths.map(path => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = path;
      });
    });

    try {
      await Promise.all(promises);
      // Images preloaded successfully
    } catch (error) {
      // Failed to preload images - continue silently
    }
  }
};

// Glass Effect Hook
export const useGlassEffect = () => {
  return {
    glassClasses: cn(
      'backdrop-blur-lg bg-white/10 border border-white/20',
      'rounded-xl shadow-xl'
    )
  };
};

// Service Tab Animation Hook
export const useServiceTabAnimation = () => {
  return {
    tabClasses: cn(
      'transition-all duration-300 ease-in-out',
      'hover:scale-105 hover:shadow-lg'
    )
  };
};
